import axios from "axios";
import api from "@/utils/axios";
import { defineStore } from "pinia";

// Route/menu access rules, declared in route meta (see router/index.ts).
//   permissions: customer feature gates that must ALL be 1 ({stock:1, Parcels:1, ...}).
//                Applies to staff too — a staff member can never exceed the customer's features.
//   staffCodes:  staff action codes that must ALL be granted (["STOCK", ...]). Ignored for the account owner.
//   ownerOnly:   account owner only (API keys, webhooks, bank details, identity documents...).
// NOTE: this only hides UI. The API enforces the same rules (Back/routes/api.php, "access:" middleware).
export interface AccessRule {
  permissions?: string[];
  staffCodes?: string[];
  ownerOnly?: boolean;
}

// Deduplicates concurrent fetchPermissions() calls (startup, navigation, focus).
let inFlight: Promise<void> | null = null;

// Plain axios: an already-expired token must not trigger the app-wide 401 redirect.
async function revokeToken(token: string): Promise<void> {
  try {
    await axios.post(`${process.env.VUE_APP_API_URL}/api/auth/client/user_logout`, null, {
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    // already invalid/expired — nothing to revoke
  }
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    token: null as string | null,
    user: null as Record<string, any> | null,
    // Always stored as { permKey: 1|0 }
    permissions: {} as Record<string, number>,
    // Staff-only: array of individual action codes ["FACTURES", "STOCK", ...]
    staffCodes: [] as string[],
    // ── Sub Accounts (Business Account feature, account owners only) ──
    // The parent's token, kept while one of its sub accounts is active in
    // this tab (`token` is then the sub account's). null = not switched.
    rootToken: null as string | null,
    // The parent (Business Account) and its sub accounts, always fetched
    // with the parent's token so the list stays the same in every account.
    rootAccount: null as Record<string, any> | null,
    subAccounts: [] as Record<string, any>[],
    // Set when someone logged in directly with a sub account's credentials.
    parentAccount: null as Record<string, any> | null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token,
    isStaff: (state) => !!state.user?.is_staff || !!state.user?.customer_id,
    isInSubAccount: (state) => !!state.rootToken,
    canSwitchAccounts(): boolean {
      return !this.isStaff && !!this.rootAccount && this.subAccounts.length > 0;
    },
    // Parent first, then its sub accounts.
    accounts(): Record<string, any>[] {
      return this.rootAccount ? [this.rootAccount, ...this.subAccounts] : [];
    },
    // The top-level account whose logo is always shown.
    brandAccount(): Record<string, any> | null {
      return this.parentAccount ?? this.rootAccount ?? this.user;
    },
  },

  actions: {
    setToken(token: string) {
      this.token = token;
    },
    setUser(user: Record<string, any>) {
      this.user = user;
    },
    clearAuth() {
      this.token = null;
      this.user = null;
      this.permissions = {};
      this.staffCodes = [];
      this.rootToken = null;
      this.rootAccount = null;
      this.subAccounts = [];
      this.parentAccount = null;
    },

    async fetchSubAccounts(): Promise<void> {
      if (!this.token || this.isStaff) return;
      try {
        const res = await api.get(`/api/auth/client/SubAccounts`, {
          headers: { Authorization: `Bearer ${this.rootToken ?? this.token}` },
        });
        this.rootAccount = res.data?.account ?? null;
        this.subAccounts = Array.isArray(res.data?.sub_accounts) ? res.data.sub_accounts : [];
        this.parentAccount = res.data?.parent ?? null;
      } catch (error) {
        console.error("Failed to fetch sub accounts:", error);
      }
    },

    // Makes `id` (the parent or one of its sub accounts) the active account
    // in this tab. The parent's token is kept aside to come back later.
    async enterAccount(id: number | string): Promise<void> {
      const rootToken = this.rootToken ?? this.token;
      if (!rootToken) return;
      const previousSubToken = this.rootToken ? this.token : null;

      if (this.rootAccount && String(id) === String(this.rootAccount.id)) {
        this.token = rootToken;
        this.rootToken = null;
      } else {
        const res = await api.get(`/api/auth/client/SwitchToSubAccount/${id}`, {
          headers: { Authorization: `Bearer ${rootToken}` },
        });
        if (res.data?.msg !== "success" || !res.data?.access_token) {
          throw new Error(res.data?.msg || "switch failed");
        }
        this.token = res.data.access_token;
        this.rootToken = rootToken;
      }

      if (previousSubToken && previousSubToken !== this.token) revokeToken(previousSubToken);

      this.user = {};
      this.permissions = {};
      this.staffCodes = [];
      await this.fetchPermissions();
    },

    // Revokes the session's tokens server-side, then clears local state. Local
    // state is cleared even if a request fails so the user is never stuck logged in.
    async logout() {
      const tokens = [this.token, this.rootToken].filter((t): t is string => !!t);
      await Promise.all(tokens.map(revokeToken));
      this.clearAuth();
    },

    // Accepts either object {key: 1|0} or string[] from login responses
    setPermissions(permissions: Record<string, any> | string[]) {
      if (Array.isArray(permissions)) {
        const obj: Record<string, number> = {};
        permissions.forEach((k) => { obj[k] = 1; });
        this.permissions = obj;
      } else {
        this.permissions = permissions ?? {};
      }
    },

    fetchPermissions(): Promise<void> {
      if (!this.token || !this.user) return Promise.resolve();
      if (inFlight) return inFlight;

      inFlight = (async () => {
        try {
          const res = await api.get(`/api/auth/client/fecthPerms`);

          // Always refresh the stored user so is_staff stays current
          if (res.data.user) this.user = { ...this.user, ...res.data.user };

          // Computed after the merge above so a freshly-set placeholder user
          // (e.g. from a staff-link bootstrap) resolves correctly on the first call.
          if (this.isStaff) {
            // permissions = customer's feature gates {stock:1, Parcels:1, ...}
            this.permissions = res.data.user?.perms ?? {};
            // staffCodes = staff's individual action codes ["STOCK", "BON_LIVRAISON", ...]
            this.staffCodes = Array.isArray(res.data.data) ? res.data.data : [];
          } else {
            this.permissions = (res.data.data && typeof res.data.data === "object" && !Array.isArray(res.data.data))
              ? res.data.data
              : {};
          }
        } catch (error) {
          console.error("Failed to fetch permissions:", error);
        } finally {
          inFlight = null;
        }
      })();
      return inFlight;
    },

    can(key: string) {
      return this.permissions[key] === 1;
    },

    // Single source of truth for both the router guard and the sidebar.
    canAccess(rule: AccessRule | undefined): boolean {
      if (!rule) return true;
      if (rule.ownerOnly && this.isStaff) return false;
      const perms = rule.permissions ?? [];
      if (!perms.every((p) => this.permissions[p] === 1)) return false;
      if (this.isStaff) {
        const codes = rule.staffCodes ?? [];
        if (!codes.every((c) => this.staffCodes.includes(c))) return false;
      }
      return true;
    },
  },

  persist: true,
});
